import { useState } from 'react';
import { format, parse, addMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Clock, Plus, Trash2, User, Calendar } from 'lucide-react';
import {
  CalendarProfessional,
  CalendarServiceType,
  CalendarSlot,
  SlotCreationParams,
} from '../../types/calendar';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  Badge,
} from '../ui';

interface DaySlotManagerProps {
  date: string;
  professionals: CalendarProfessional[];
  serviceTypes: CalendarServiceType[];
  slots: CalendarSlot[];
  selectedProfessionalId: number | null;
  onCreateSlot: (data: Partial<CalendarSlot>) => Promise<void>;
  onCreateMultipleSlots: (params: SlotCreationParams) => Promise<void>;
  onDeleteSlot: (id: number) => Promise<void>;
  isLoading: boolean;
}

function DaySlotManager({
  date,
  professionals,
  serviceTypes,
  slots,
  selectedProfessionalId,
  onCreateSlot,
  onCreateMultipleSlots,
  onDeleteSlot,
  isLoading,
}: DaySlotManagerProps) {
  const [professionalId, setProfessionalId] = useState<number>(selectedProfessionalId || 0);
  const [startTime, setStartTime] = useState<string>('08:00');
  const [endTime, setEndTime] = useState<string>('09:00');
  const [slotDuration, setSlotDuration] = useState<number>(30);
  const [breakBetween, setBreakBetween] = useState<number>(0);

  const handleCreateSingleSlot = async () => {
    if (!professionalId) return;

    // Ensure we're using the date in YYYY-MM-DD format without timezone conversion
    const formattedDate = date.split('T')[0];

    await onCreateSlot({
      professional_id: professionalId,
      date: formattedDate,
      start_time: startTime,
      end_time: endTime,
      status: 'available',
    });
  };

  const handleCreateMultipleSlots = async () => {
    if (!professionalId) return;

    // Ensure we're using the date in YYYY-MM-DD format without timezone conversion
    const formattedDate = date.split('T')[0];

    await onCreateMultipleSlots({
      professional_id: professionalId,
      date: formattedDate,
      start_time: startTime,
      end_time: endTime,
      slot_duration: slotDuration,
      break_between: breakBetween,
    });
  };

  const getSlotStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge variant="success">Disponível</Badge>;
      case 'booked':
        return <Badge variant="warning">Reservado</Badge>;
      case 'confirmed':
        return <Badge variant="info">Confirmado</Badge>;
      case 'canceled':
        return <Badge variant="error">Cancelado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getProfessionalName = (id: number) => {
    const professional = professionals.find(p => p.id === id);
    return professional ? professional.name : 'Profissional não encontrado';
  };

  // Group slots by professional
  const slotsByProfessional: Record<number, CalendarSlot[]> = {};
  slots.forEach(slot => {
    if (!slotsByProfessional[slot.professional_id]) {
      slotsByProfessional[slot.professional_id] = [];
    }
    slotsByProfessional[slot.professional_id].push(slot);
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create Single Slot */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Adicionar Horário Individual</h3>
          <FormField>
            <FormLabel htmlFor="professional_id" required>
              Profissional
            </FormLabel>
            <FormSelect
              id="professional_id"
              value={professionalId || ''}
              onChange={e => setProfessionalId(Number(e.target.value))}
              required
            >
              <option value="">Selecione um profissional</option>
              {professionals.map(professional => (
                <option key={professional.id} value={professional.id}>
                  {professional.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="start_time" required>
                Horário Início
              </FormLabel>
              <FormInput
                id="start_time"
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                required
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="end_time" required>
                Horário Fim
              </FormLabel>
              <FormInput
                id="end_time"
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                required
              />
            </FormField>
          </div>

          <Button
            onClick={handleCreateSingleSlot}
            disabled={!professionalId || isLoading}
            isLoading={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Horário
          </Button>
        </div>

        {/* Create Multiple Slots */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Gerar Múltiplos Horários</h3>
          <FormField>
            <FormLabel htmlFor="slot_duration" required>
              Duração de cada slot (minutos)
            </FormLabel>
            <FormInput
              id="slot_duration"
              type="number"
              min="5"
              step="5"
              value={slotDuration}
              onChange={e => setSlotDuration(Number(e.target.value))}
              required
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="break_between">Intervalo entre slots (minutos)</FormLabel>
            <FormInput
              id="break_between"
              type="number"
              min="0"
              step="5"
              value={breakBetween}
              onChange={e => setBreakBetween(Number(e.target.value))}
            />
          </FormField>

          <Button
            onClick={handleCreateMultipleSlots}
            disabled={!professionalId || isLoading}
            isLoading={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Gerar Múltiplos Horários
          </Button>
        </div>
      </div>

      {/* Existing Slots */}
      <Card>
        <CardHeader>
          <CardTitle>Horários Disponíveis</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(slotsByProfessional).length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              Nenhum horário cadastrado para esta data
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(slotsByProfessional).map(([profId, profSlots]) => (
                <div key={profId} className="space-y-2">
                  <h3 className="font-medium flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    {getProfessionalName(Number(profId))}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {profSlots.map(slot => (
                      <div
                        key={slot.id}
                        className="flex justify-between items-center p-2 border rounded-md bg-gray-50"
                      >
                        <div>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1 text-gray-500" />
                            <span className="text-sm font-medium">
                              {slot.start_time} - {slot.end_time}
                            </span>
                          </div>
                          <div className="mt-1">{getSlotStatusBadge(slot.status)}</div>
                        </div>
                        {slot.status === 'available' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteSlot(slot.id)}
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DaySlotManager;
